import React from 'react';
import ProfilePicture from './ProfilePicture';
import './CollectionOfficerCard.css';

interface CollectionOfficer {
  userno: number;
  username: string;
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  full_name: string;
}

interface OfficerMetrics {
  total_loans: number;
  current_loans: number;
  past_due_loans: number;
  one_to_10: number;
  eleven_to_30: number;
  thirty_one_to_60: number;
  sixty_one_to_90: number;
  ninety_one_to_120: number;
  one_twenty_one_plus: number;
  thirty_plus: number;
  total_balance: number;
  pct_current: number;
  pct_past_due: number;
  pct_one_to_10: number;
  pct_eleven_to_30: number;
  pct_thirty_one_to_60: number;
  pct_sixty_one_to_90: number;
  pct_ninety_one_to_120: number;
  pct_one_twenty_one_plus: number;
  pct_thirty_plus: number;
}

interface CollectionOfficerCardProps {
  officer: CollectionOfficer;
  metrics: OfficerMetrics;
  onOfficerClick: (officer: CollectionOfficer) => void;
  onBucketClick: (officer: CollectionOfficer, bucket: string) => void;
  loading?: boolean;
}

const CollectionOfficerCard: React.FC<CollectionOfficerCardProps> = ({
  officer,
  metrics,
  onOfficerClick,
  onBucketClick,
  loading = false
}) => {
  // Debug logging for officer email
  console.log(`🔍 CollectionOfficerCard: Officer ${officer.full_name} has email: "${officer.email}"`);
  
  // Handle null/empty emails
  const hasValidEmail = officer.email && officer.email !== 'null' && officer.email.trim() !== '';
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPercent = (value: number): string => {
    return `${value.toFixed(1)}%`;
  };

  // Note: ProfilePicture component will handle photo loading and fallbacks

  const getHealthColor = (pctPastDue: number): string => {
    if (pctPastDue < 5) return '#10b981'; // green
    if (pctPastDue < 15) return '#f59e0b'; // yellow
    return '#ef4444'; // red
  };

  return (
    <div 
      className="officer-card"
      onClick={() => onOfficerClick(officer)}
    >
      <div className="officer-header">
        <div className="officer-photo" title={hasValidEmail ? `Profile picture for ${officer.full_name}` : `No email available for ${officer.full_name}`}>
          <ProfilePicture 
            size={60}
            showBorder={true}
            borderColor="#e5e7eb"
            userEmail={hasValidEmail ? officer.email : undefined}
          />
          {!hasValidEmail && (
            <div className="no-email-indicator" title="No email address available">
              ⚠️
            </div>
          )}
        </div>
        <div className="officer-info">
          <h4 className="officer-name">{officer.full_name}</h4>
          <p className="officer-username">@{officer.username}</p>
          <p className="officer-email">{officer.email}</p>
        </div>
        <div className="health-indicator">
          <div 
            className="health-dot"
            style={{ backgroundColor: getHealthColor(metrics.pct_past_due) }}
          ></div>
        </div>
      </div>

      <div className="officer-metrics">
        <div className="metric-row">
          <div className="metric-item">
            <span className="metric-label">Total Loans</span>
            <span className="metric-value">{metrics.total_loans.toLocaleString()}</span>
          </div>
          <div className="metric-item">
            <span className="metric-label">Total Balance</span>
            <span className="metric-value">{formatCurrency(metrics.total_balance)}</span>
          </div>
        </div>

        <div className="metric-row">
          <div className="metric-item">
            <span className="metric-label">Current</span>
            <span className="metric-value current">{formatPercent(metrics.pct_current)}</span>
          </div>
          <div className="metric-item">
            <span className="metric-label">Past Due</span>
            <span className="metric-value past-due">{formatPercent(metrics.pct_past_due)}</span>
          </div>
        </div>

        <div className="past-due-breakdown">
          <div 
            className="breakdown-item clickable" 
            onClick={(e) => {
              e.stopPropagation();
              onBucketClick(officer, '1-10');
            }}
            title="Click to view accounts in this bucket"
          >
            <span className="breakdown-label">1-10 days</span>
            <span className="breakdown-value">{metrics.one_to_10}</span>
          </div>
          <div 
            className="breakdown-item clickable" 
            onClick={(e) => {
              e.stopPropagation();
              onBucketClick(officer, '11-30');
            }}
            title="Click to view accounts in this bucket"
          >
            <span className="breakdown-label">11-30 days</span>
            <span className="breakdown-value">{metrics.eleven_to_30}</span>
          </div>
          <div 
            className="breakdown-item clickable" 
            onClick={(e) => {
              e.stopPropagation();
              onBucketClick(officer, '31-60');
            }}
            title="Click to view accounts in this bucket"
          >
            <span className="breakdown-label">31-60 days</span>
            <span className="breakdown-value">{metrics.thirty_one_to_60}</span>
          </div>
          <div 
            className="breakdown-item clickable" 
            onClick={(e) => {
              e.stopPropagation();
              onBucketClick(officer, '61-90');
            }}
            title="Click to view accounts in this bucket"
          >
            <span className="breakdown-label">61-90 days</span>
            <span className="breakdown-value">{metrics.sixty_one_to_90}</span>
          </div>
          <div 
            className="breakdown-item clickable" 
            onClick={(e) => {
              e.stopPropagation();
              onBucketClick(officer, '91-120');
            }}
            title="Click to view accounts in this bucket"
          >
            <span className="breakdown-label">91-120 days</span>
            <span className="breakdown-value">{metrics.ninety_one_to_120}</span>
          </div>
          <div 
            className="breakdown-item clickable" 
            onClick={(e) => {
              e.stopPropagation();
              onBucketClick(officer, '120+');
            }}
            title="Click to view accounts in this bucket"
          >
            <span className="breakdown-label">120+ days</span>
            <span className="breakdown-value">{metrics.one_twenty_one_plus}</span>
          </div>
          <div 
            className="breakdown-item clickable" 
            onClick={(e) => {
              e.stopPropagation();
              onBucketClick(officer, '30+');
            }}
            title="Click to view all accounts 30+ days past due"
          >
            <span className="breakdown-label">30+ days</span>
            <span className="breakdown-value">{metrics.thirty_plus}</span>
          </div>
        </div>
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}

      <div className="click-hint">
        <span>Click to view detailed accounts</span>
      </div>
    </div>
  );
};

export default CollectionOfficerCard;
